<template>
  <el-dialog
    :title="$t('Label.Field Settings')"
    :visible.sync="internalVisible"
    @close="emitClose"
  >
    <div class="process_field_setting_notice">{{ $t('Message.Process Field Setting') }}</div>
    <div class="standard_dialog_tabs">
      <el-tabs v-model="internalTabGroup" type="card" class="dialog-tabs">
        <el-tab-pane
          v-for="tab in settableTabs"
          :key="tab.group"
          :label="$t(`Label.${tab.label}`)"
          :name="tab.group"
        />
      </el-tabs>
    </div>
    <div class="standard_transfer_wrapper">
      <div class="standard_transfer">
        <el-transfer
          class="transfer_body"
          v-model="workingVisibleKeys"
          :data="sortedTransferData"
          filterable
          :titles="[$t('Label.Hidden'), $t('Label.Visible')]"
          :props="{ key: 'key', label: 'label', disabled: 'required' }"
          @right-check-change="onRightCheckChange"
        />
      </div>
      <div class="order_buttons">
        <el-button type="primary" size="small" icon="el-icon-arrow-up" :disabled="!canMoveUp()" @click="moveCheckedUp"/>
        <el-button type="primary" size="small" icon="el-icon-arrow-down" :disabled="!canMoveDown()" @click="moveCheckedDown"/>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="resetDefault">{{$t('Label.Reset Column')}}</el-button>
      <el-button @click="emitClose">{{$t('Label.Close Cancel')}}</el-button>
      <el-button type="primary" @click="saveAllTabs">{{$t('Label.Save')}}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import {LANGUAGE_SETTING} from 'lib-tw-common';

export default {
  name: "TwStandardFieldSettingDialog",
  props: {
    visible: { type: Boolean, default: false },
    processId: { type: String, required: true },
    schemas: { type: Object, required: true },
    standardTabs: { type: Array, required: true },
    initialTabGroup: { type: String, required: true }
  },
  data() {
    return {
      internalTabGroup: "",
      transferDataByTab: {
        Overview: [],
        Tracking: [],
        Schedule: [],
        Transship: [],
        Others: []
      },
      workingVisibleKeys: [],
      workingVisibleByTab: {
        Overview: [],
        Tracking: [],
        Schedule: [],
        Transship: [],
        Others: []
      },
      rightCheckedKeys: []
    };
  },
  computed: {
    settableTabs() {
      const excluded = new Set(['Container Details', 'Goods Details']);
      return this.standardTabs.filter(tab => !excluded.has(tab.group));
    },
    internalVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) this.emitClose();
      }
    },
    storeLayout() {
      return this.$store.getters.getStandardFieldLayout(this.processId) || null;
    },
    isEnglish() {
      return this.$store.state.language === LANGUAGE_SETTING.ENGLISH;
    },
    // 並び替えを反映したtransferデータ
    sortedTransferData() {
      const originalData = this.transferDataByTab[this.internalTabGroup] || [];
      const visibleKeysSet = new Set(this.workingVisibleKeys);

      // dataをマップに変換（keyで検索しやすくする）
      const dataMap = new Map(originalData.map(item => [item.key, item]));

      // 表示側：workingVisibleKeysの順序に従う
      const visibleItems = this.workingVisibleKeys
        .map(key => dataMap.get(key))
        .filter(Boolean);

      // 非表示側：元の順序を保持
      const hiddenItems = originalData.filter(item => !visibleKeysSet.has(item.key));

      // 表示側を先に、その後非表示側を配置
      return [...visibleItems, ...hiddenItems];
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(isOpen) {
        if (!isOpen) return;
        // transferデータ生成（タブごと）
        this.transferDataByTab = this.buildTransferDataByTab(this.schemas);
        // 作業用 visibleByTab を storeLayout から初期化
        this.workingVisibleByTab = this.buildWorkingVisibleByTabFromLayout(
          this.storeLayout,
          this.transferDataByTab
        );
         // 初期タブ
        this.internalTabGroup = this.initialTabGroup;
        // 現在タブの visible を transfer に反映
        this.workingVisibleKeys = this.workingVisibleByTab[this.internalTabGroup].slice();
      }
    },

    // タブ切替時：transfer表示を切り替える
    internalTabGroup(newTabGroup, oldTabGroup) {
      // oldTabGroup の作業内容を保存
      if (oldTabGroup) {
        this.workingVisibleByTab[oldTabGroup] = this.workingVisibleKeys.slice();
      }
      // newTabGroup の内容を表示
      this.workingVisibleKeys = this.workingVisibleByTab[newTabGroup].slice();
    },

    // transfer操作時：現在タブの作業状態に反映
    workingVisibleKeys(newKeys) {
      this.workingVisibleByTab[this.internalTabGroup] = newKeys.slice();
    }
  },
  methods: {
    emitClose() {
      this.$emit("close");
    },

    buildTransferDataByTab(schemas) {
      const result = { Overview: [], Tracking: [], Schedule: [], Transship: [], Others: [] };
      const tables = schemas && Array.isArray(schemas.tables) ? schemas.tables : [];

      tables.forEach(table => {
        (table.groups || []).forEach(group => {
          (group.children || []).forEach(item => {
            if (!item || !item.key) return;
            if (this.isForceHiddenStandardItem(item)) return;

            const standardGroup = typeof item.standardGroup === "string" ? item.standardGroup : "";
            if (standardGroup === "") return;
            if (!result[standardGroup]) return;

            result[standardGroup].push({
              key: item.key,
              label: this.isEnglish ? item.label : item.jpLabel,
              tabNo: item.tabNo,
              required: !!item.required
            });
          });
        });
      });

      // TrackingタブにcontainerNoを追加（スキーマのstandardGroupに依存しない）
      if (!result['Tracking'].some(x => x.key === 'containerNo')) {
        for (const table of tables) {
          if (table.variableName !== 'linkageTransportation') continue;
          for (const group of (table.groups || [])) {
            if (group.variableName !== 'containerInfoGrp') continue;
            const containerNoItem = (group.children || []).find(c => c.key === 'containerNo');
            if (containerNoItem) {
              result['Tracking'].push({
                key: containerNoItem.key,
                label: this.isEnglish ? containerNoItem.label : containerNoItem.jpLabel,
                tabNo: 3,
                required: !!containerNoItem.required
              });
            }
            break;
          }
          break;
        }
      }

      // 初期表示の整合性のため、transferの候補も tabNo順にしておく
      Object.keys(result).forEach(tabGroup => {
        result[tabGroup] = result[tabGroup]
          .slice()
          .sort((a, b) => (a.tabNo == null ? Infinity : Number(a.tabNo)) - (b.tabNo == null ? Infinity : Number(b.tabNo)));
      });

      return result;
    },

    buildWorkingVisibleByTabFromLayout(layout, transferDataByTab) {
      const empty = { Overview: [], Tracking: [], Schedule: [], Transship: [], Others: [] };

      // storeに無い場合：全表示（tabNo順の候補順）
      if (!layout) {
        const allVisible = {};
        Object.keys(empty).forEach(tabGroup => {
          allVisible[tabGroup] = (transferDataByTab[tabGroup] || []).map(x => x.key);
        });
        return allVisible;
      }

      // storeがある場合：スキーマ差分に追従しつつ visible を作る
      const result = {};
      Object.keys(empty).forEach(tabGroup => {
        const allKeys = (transferDataByTab[tabGroup] || []).map(x => x.key);

        const savedTab = layout[tabGroup] || { visible: [], hidden: [] };
        const savedVisible = Array.isArray(savedTab.visible) ? savedTab.visible : [];
        const savedHidden = Array.isArray(savedTab.hidden) ? savedTab.hidden : [];

        const allKeySet = new Set(allKeys);

        // 必須項目のキーセット
        const requiredKeySet = new Set(
          (transferDataByTab[tabGroup] || []).filter(x => x.required).map(x => x.key)
        );

        // スキーマに存在するものだけ残す（必須項目は常に表示側に移す）
        const visibleFiltered = savedVisible.filter(k => allKeySet.has(k));
        const hiddenFiltered = savedHidden.filter(k => allKeySet.has(k) && !requiredKeySet.has(k));

        // 保存済みhiddenにあった必須項目を表示側末尾へ追加
        const requiredFromHidden = savedHidden.filter(k => allKeySet.has(k) && requiredKeySet.has(k));
        visibleFiltered.push(...requiredFromHidden);

        // visible/hiddenに無い新規を visible 末尾へ
        const usedSet = new Set([...visibleFiltered, ...hiddenFiltered]);
        const newKeys = allKeys.filter(k => !usedSet.has(k));

        result[tabGroup] = visibleFiltered.concat(newKeys);
      });

      return result;
    },

    saveAllTabs() {
      // タブ切替直前の状態が残っていない事故を防ぐ
      this.workingVisibleByTab[this.internalTabGroup] = this.workingVisibleKeys.slice();

      // 現在の storeLayout をベースにする（無ければ空を作る）
      const base = this.storeLayout || {
        Overview: { visible: [], hidden: [] },
        Tracking: { visible: [], hidden: [] },
        Schedule: { visible: [], hidden: [] },
        Transship: { visible: [], hidden: [] },
        Others: { visible: [], hidden: [] }
      };

      const nextLayout = JSON.parse(JSON.stringify(base));

      // 全タブ分、hidden を再計算して保存
      Object.keys(this.transferDataByTab).forEach(tabGroup => {
        const allKeys = (this.transferDataByTab[tabGroup] || []).map(x => x.key);
        const visibleRaw = (this.workingVisibleByTab[tabGroup] || []).slice();
        const visibleSet = new Set(visibleRaw);

        // 並び替えた順序を保持するため、visibleRawの順序をそのまま使う
        const allKeySet = new Set(allKeys);
        const visible = visibleRaw.filter(k => allKeySet.has(k));

        const requiredKeySet = new Set(
          (this.transferDataByTab[tabGroup] || []).filter(x => x.required).map(x => x.key)
        );
        const hidden = allKeys.filter(k => !visibleSet.has(k) && !requiredKeySet.has(k));

        nextLayout[tabGroup] = { visible, hidden };
      });

      this.$store.commit("SET_STANDARD_FIELD_LAYOUT", {
        processId: this.processId,
        layout: nextLayout
      });

      this.emitClose();
    },
    isForceHiddenStandardItem(item) {
      const code = item?.code;
      return code === 'SYS_MST_UNIT' || code === 'SYS_MST_CURRENCY';
    },
    resetDefault() {
      const nextWorkingVisibleTab = {};

      Object.keys(this.transferDataByTab).forEach((tabGroup) => {
        const list = (this.transferDataByTab[tabGroup] || []).slice();

        nextWorkingVisibleTab[tabGroup] = list.map(x => x.key);
      });
      this.workingVisibleByTab = nextWorkingVisibleTab;
      const currentGroup = this.internalTabGroup;
      this.workingVisibleKeys = (this.workingVisibleByTab[currentGroup] || []).slice();
    },
    onRightCheckChange(checkedKeys) {
      this.rightCheckedKeys = checkedKeys || [];
    },

    // ↑：選択されたものを上へ
    moveCheckedUp() {
      const selectedKeys = new Set(this.rightCheckedKeys);
      if (!selectedKeys.size) return;

      const updatedKeys = this.workingVisibleKeys.slice();

      for (let i = 1; i < updatedKeys.length; i++) {
        const currentKey = updatedKeys[i];
        const previousKey = updatedKeys[i - 1];
        if (selectedKeys.has(currentKey) && !selectedKeys.has(previousKey)) {
          updatedKeys[i - 1] = currentKey;
          updatedKeys[i] = previousKey;
        }
      }
      this.workingVisibleKeys = updatedKeys;
    },

    // ↓：選択されたものを下へ
    moveCheckedDown() {
      const selectedKeys = new Set(this.rightCheckedKeys);
      if (!selectedKeys.size) return;

      const updatedKeys = this.workingVisibleKeys.slice();

      for (let i = updatedKeys.length - 2; i >= 0; i--) {
        const currentKey = updatedKeys[i];
        const nextKey = updatedKeys[i + 1];
        if (selectedKeys.has(currentKey) && !selectedKeys.has(nextKey)) {
          updatedKeys[i + 1] = currentKey;
          updatedKeys[i] = nextKey;
        }
      }
      this.workingVisibleKeys = updatedKeys;
    },

    canMoveUp() {
      const selectedKeys = new Set(this.rightCheckedKeys);
      if (!selectedKeys.size) return false;
      const visibleKeys = this.workingVisibleKeys;
      for (let i = 1; i < visibleKeys.length; i++) {
        if (selectedKeys.has(visibleKeys[i]) && !selectedKeys.has(visibleKeys[i - 1])) {
          return true;
        }
      }
      return false;
    },

    canMoveDown() {
      const selectedKeys = new Set(this.rightCheckedKeys);
      if (!selectedKeys.size) return false;
      const visibleKeys = this.workingVisibleKeys;
      for (let i = visibleKeys.length - 2; i >= 0; i--) {
        if (selectedKeys.has(visibleKeys[i]) && !selectedKeys.has(visibleKeys[i + 1])) {
          return true;
        }
      }
      return false;
    },
  }
};
</script>

<style lang="scss" scoped>
  .process_field_setting_notice {
    font-size: 14px;
    color: $color_black;
    margin-top: -20px;
    margin-bottom: 10px;
  }
  .dialog-tabs {
    width: 100%;
    margin-bottom: 12px;
  }
  .standard_dialog_tabs {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
  }
  .standard_dialog_tabs ::v-deep .el-tabs__header {
    margin: 0;
  }
  .standard_dialog_tabs ::v-deep .el-tabs__nav-wrap {
    width: 100%;
  }
  .standard_dialog_tabs ::v-deep .el-tabs__nav {
    float: none;
    display: flex;
    width: 100%;
  }
  .standard_dialog_tabs ::v-deep .el-tabs__item {
    flex: 1;
    text-align: center;
  }
  .transfer_body {
    width: 100%;
  }
  .standard_transfer_wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .standard_transfer {
    flex: 1;
    position: relative;
  }
  .standard_transfer ::v-deep .el-transfer {
    width: 100%;
    display: flex;
    align-items: center;
  }
  .standard_transfer ::v-deep .el-transfer-panel {
    width: calc(50% - 60px);
  }
  .standard_transfer ::v-deep .el-transfer__buttons {
    display: flex;
    flex-direction: row;
    gap: 8px;
    padding: 0 10px;
    flex-wrap: nowrap;
  }
  .standard_transfer ::v-deep .el-transfer__buttons .el-button {
    min-width: 40px;
    width: 40px;
    height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .standard_transfer ::v-deep .el-transfer__buttons .el-button + .el-button {
    margin-left: 0;
    margin-top: 0;
  }
  .order_buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .order_buttons .el-button {
    min-width: 40px;
    width: 40px !important;
    height: 36px;
    padding: 0 !important;
    margin: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    line-height: 1;
  }
  .order_buttons .el-button > span {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>

<style lang="scss">
  .dialog-tabs .el-tabs__item {
    color: $color_gray_600;
    background: $color_gray_300;
  }
  .dialog-tabs .el-tabs__item.is-active {
    color: $color_gray_800;
    background: #fff;
  }
</style>
